/**
 * Design brief telemetry events (v1.8.0).
 *
 * Canonical event names — per `feedback_unified_event_taxonomy` they MUST
 * NOT be renamed in any adapter. Adapters forward these names as-is to
 * Amplitude / Mixpanel / GA4 / FB / TikTok.
 *
 * Wiring: backend handlers emit via `logger.info('telemetry', name, props)`
 * and the existing log → telemetry forwarder picks them up. Frontend
 * components call `emit*` helpers from this module.
 *
 * To actually fire to Amplitude from the frontend, this module currently
 * uses a `dataLayer`-style push so the analytics consolidator can attach
 * a real Amplitude SDK later without changing call sites.
 */

export type AssetMode = 'plan_and_generate' | 'plan_only' | 'text_only';

export interface DesignBriefBaseProps {
  postId: string;
  projectId: string;
  platform?: string;
  formatId?: string;
  designFunction?: string;
  narrativeShape?: 'linear' | 'standalone' | 'coverOnly';
  assetMode?: AssetMode;
  briefStatus?: string;
}

export interface DesignAcceptanceFailedProps extends DesignBriefBaseProps {
  failedCriteria: string[];
  attempt: 1 | 2;
}

export interface DesignPhaseFailedProps extends DesignBriefBaseProps {
  phase: string;
  error: string;
}

export interface PostRegeneratedFromBriefProps extends DesignBriefBaseProps {
  postKind: 'master' | 'derivative' | 'direct';
  staleDerivativesCount: number;
}

export interface AssetModeChangedProps {
  source: 'ui' | 'env' | 'legacy_flags';
  newValue: AssetMode;
  oldValue?: AssetMode;
}

interface TrackedEvent {
  name: string;
  props: Record<string, unknown>;
  ts: number;
}

declare global {
  interface Window {
    segmentlyDesignBriefEvents?: TrackedEvent[];
  }
}

function push(name: string, props: Record<string, unknown>): void {
  const evt: TrackedEvent = { name, props, ts: Date.now() };
  if (typeof window !== 'undefined') {
    if (!window.segmentlyDesignBriefEvents) window.segmentlyDesignBriefEvents = [];
    window.segmentlyDesignBriefEvents.push(evt);
  }
  // Backend log channel (silent in browser).
  if (typeof console !== 'undefined' && typeof console.info === 'function') {
    console.info(`[telemetry] ${name}`, props);
  }
}

export function emitDesignBriefGenerated(props: DesignBriefBaseProps): void {
  push('DesignBriefGenerated', props as unknown as Record<string, unknown>);
}

export function emitDesignBriefRegenerated(props: DesignBriefBaseProps): void {
  push('DesignBriefRegenerated', props as unknown as Record<string, unknown>);
}

export function emitPostRegeneratedFromBrief(props: PostRegeneratedFromBriefProps): void {
  push('PostRegeneratedFromBrief', props as unknown as Record<string, unknown>);
}

export function emitDesignAcceptanceFailed(props: DesignAcceptanceFailedProps): void {
  push('DesignAcceptanceFailed', props as unknown as Record<string, unknown>);
}

export function emitDesignPhaseFailed(props: DesignPhaseFailedProps): void {
  push('DesignPhaseFailed', props as unknown as Record<string, unknown>);
}

export function emitAssetModeChanged(props: AssetModeChangedProps): void {
  push('AssetModeChanged', props as unknown as Record<string, unknown>);
}
